import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { EyeOff } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { signUp } from "@/api/auth";
import { legalDocumentsFor } from "@/lib/legalVersions";
import { logger } from "@/lib/logger";
import { getErrorMessage } from "@/lib/getErrorMessage";
import { capturePosthog } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { AuthCard } from "./AuthCard";
import { AuthSegmentedTabs } from "./AuthSegmentedTabs";
import { ValidatedField } from "./ValidatedField";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import { useFieldValidation } from "./useFieldValidation";
import { StoreIcon, GiftIcon } from "@/components/icons/domain";
import { PremiumIcon, LockIcon } from "@/components/icons/status";
import { ViewIcon, MailIcon } from "@/components/icons/actions";
import { ProfileIcon } from "@/components/icons/nav";

const emailSchema = z.string().email("Email invalide");
const passwordSchema = z
  .string()
  .min(8, "Le mot de passe doit contenir au moins 8 caractères")
  .regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
  .regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre");

interface SignupFormProps {
  onSwitchMode: (mode: "login") => void;
}

export function SignupForm({ onSwitchMode }: SignupFormProps) {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref");
  const [signupProfile, setSignupProfile] = useState<"manager" | "employee">("manager");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalTouched, setLegalTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const legalMissing = legalTouched && !legalAccepted;

  const firstNameValidation = useFieldValidation({ value: firstName, required: true, requiredMessage: "Le prénom est requis" });
  const lastNameValidation = useFieldValidation({ value: lastName, required: true, requiredMessage: "Le nom est requis" });
  const storeNameValidation = useFieldValidation({ value: storeName, required: true, requiredMessage: "Le nom du magasin est requis" });
  const emailValidation = useFieldValidation({ value: email, schema: emailSchema, required: true });
  const passwordValidation = useFieldValidation({ value: password, schema: passwordSchema });

  const analyticsProperties = () => ({
    signup_profile: signupProfile,
    role_target: signupProfile,
    entry_mode: "signup",
    source_path: "/auth",
    source_url: typeof window !== "undefined" ? window.location.href : "/auth",
    referral_code_present: Boolean(referralCode),
    referral_code: referralCode ?? null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    [firstNameValidation, lastNameValidation, storeNameValidation, emailValidation, passwordValidation].forEach((v) =>
      v.markTouched(),
    );
    setLegalTouched(true);
    const anyInvalid =
      !firstName.trim() ||
      !lastName.trim() ||
      !storeName.trim() ||
      emailValidation.state === "invalid" ||
      !passwordSchema.safeParse(password).success ||
      !legalAccepted;
    if (anyInvalid) return;

    setIsLoading(true);
    try {
      capturePosthog("auth_signup_submitted", analyticsProperties());
      const { data, error } = await signUp({
        email,
        password,
        options: {
          // Origin courant (prod, staging, localhost) : la confirmation email
          // doit ramener l'utilisateur sur l'environnement où il s'est inscrit.
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            first_name: firstName,
            last_name: lastName,
            store_name: storeName,
            ...(referralCode ? { referral_code: referralCode } : {}),
            // Versions acceptées en cochant la case : le déclencheur
            // on_auth_user_created_legal_acceptance les enregistre.
            legal_acceptance: legalDocumentsFor("client"),
          },
        },
      });
      if (error) {
        if (error.message.includes("already registered")) {
          capturePosthog("auth_signup_failed", { ...analyticsProperties(), error_type: "already_registered" });
          toast.error("Compte existant", {
            description: "Un compte existe déjà avec cet email. Connectez-vous ou utilisez un autre email.",
          });
        } else {
          throw error;
        }
        return;
      }
      if (data.user) {
        capturePosthog("auth_signup_completed", { ...analyticsProperties(), signup_user_id: data.user.id });
        toast.success("Compte créé !", {
          description:
            "Vérifiez votre boîte mail pour activer votre compte. Pensez à regarder vos spams si vous ne le trouvez pas.",
        });
      }
    } catch (err: unknown) {
      // Trace le drop-off du funnel : sans cet événement, une casse du
      // signup (ex. « Database error saving new user ») était invisible.
      capturePosthog("auth_signup_failed", {
        ...analyticsProperties(),
        error_type: "signup_error",
        error_message: err instanceof Error ? err.message : String(err),
      });
      logger.error("Auth signup error:", err);
      toast.error("Erreur", { description: getErrorMessage(err, "Une erreur est survenue. Veuillez réessayer.") });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCard
      title="Créez votre espace"
      subtitle={
        <>
          Commencez votre essai gratuit de <strong className="text-foreground">14 jours</strong>. Sans carte bancaire.
        </>
      }
      topSlot={<AuthSegmentedTabs value={signupProfile} onChange={setSignupProfile} />}
      bottomSlot={
        <p className="text-sm text-muted-foreground">
          Déjà un compte ?{" "}
          <Button
            type="button"
            variant="link"
            onClick={() => onSwitchMode("login")}
            className="px-1 h-auto text-sm font-semibold"
          >
            Se connecter
          </Button>
        </p>
      }
    >
      {signupProfile === "employee" ? (
        <div className="space-y-4 py-2">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-primary/15 motion-safe:animate-pulse" />
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/15 flex items-center justify-center">
                <MailIcon className="w-7 h-7 text-primary" aria-hidden="true" />
              </div>
            </div>
          </div>
          <div className="text-center space-y-1.5">
            <p className="text-base font-semibold text-foreground">Activation par invitation</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Votre espace salarié est activé via le lien d'invitation envoyé par votre gérant.
            </p>
          </div>
          <div className="rounded-xl bg-muted/40 border border-border/50 p-3.5">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <PremiumIcon className="inline w-3.5 h-3.5 text-primary mr-1 -mt-0.5" aria-hidden="true" />
              Pas reçu d'email ? Vérifiez vos spams ou demandez à votre gérant de renvoyer l'invitation depuis le
              registre du personnel.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {referralCode && (
            <div className="rounded-xl bg-success/10 border border-success/25 px-3.5 py-2.5 text-sm text-success flex items-center gap-2">
              <GiftIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span>
                Code <strong>{referralCode}</strong> appliqué — essai prolongé jusqu'à 30 jours 🎉
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <ValidatedField
              label="Prénom"
              leadingIcon={<ProfileIcon />}
              placeholder="Jean"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={firstNameValidation.onBlur}
              disabled={isLoading}
              autoComplete="given-name"
              validation={firstNameValidation}
              hideStatusIcon
            />
            <ValidatedField
              label="Nom"
              placeholder="Dupont"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={lastNameValidation.onBlur}
              disabled={isLoading}
              autoComplete="family-name"
              validation={lastNameValidation}
              hideStatusIcon
            />
          </div>

          <ValidatedField
            label="Nom du magasin"
            leadingIcon={<StoreIcon />}
            placeholder="Carrefour Express Paris 11"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            onBlur={storeNameValidation.onBlur}
            disabled={isLoading}
            validation={storeNameValidation}
            hideStatusIcon
          />

          <ValidatedField
            label="Email"
            type="email"
            inputMode="email"
            leadingIcon={<MailIcon />}
            placeholder="jean.dupont@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={emailValidation.onBlur}
            disabled={isLoading}
            autoComplete="email"
            validation={emailValidation}
          />

          <div>
            <ValidatedField
              label="Mot de passe"
              type={showPassword ? "text" : "password"}
              leadingIcon={<LockIcon />}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={passwordValidation.onBlur}
              disabled={isLoading}
              autoComplete="new-password"
              validation={passwordValidation}
              hideStatusIcon
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <ViewIcon className="w-4 h-4" />}
                </button>
              }
            />
            <PasswordStrengthIndicator password={password} />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="signup-legal"
                checked={legalAccepted}
                onCheckedChange={(checked) => setLegalAccepted(checked === true)}
                disabled={isLoading}
                aria-invalid={legalMissing || undefined}
                aria-describedby={legalMissing ? "signup-legal-error" : undefined}
                className="mt-0.5"
              />
              {/* Nouvel onglet : quitter la page viderait le formulaire. */}
              <label htmlFor="signup-legal" className="text-xs text-muted-foreground leading-relaxed">
                J'accepte les{" "}
                <a href="/cgv" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  conditions générales de vente
                </a>{" "}
                et les{" "}
                <a href="/cgu" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  conditions générales d'utilisation
                </a>
                , et je reconnais avoir pris connaissance de la{" "}
                <a
                  href="/confidentialite"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  politique de confidentialité
                </a>
                .
              </label>
            </div>
            {legalMissing && (
              <p id="signup-legal-error" role="alert" className="text-xs text-destructive">
                Cochez la case pour accepter les conditions.
              </p>
            )}
          </div>

          <Button
            type="submit"
            loading={isLoading}
            className={cn(
              "w-full h-12 text-base font-semibold rounded-xl mt-2 relative overflow-hidden",
              "bg-gradient-to-r from-primary to-accent hover:shadow-elevated hover:scale-[1.01]",
              "transition-[transform,box-shadow] duration-base ease-out-expo",
              "[&_*]:!text-primary-foreground",
            )}
          >
            {isLoading ? "Création du compte…" : "Créer mon compte"}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
